import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { GoalScope } from '../../../../../../pure-js/types/CoachTypes'
import { COACH_FOCUS_ROUTE_STATE_KEY } from '../../../../config/routes/routeEnums'
import { SUPPORTED_FOCUSES, readStoredFocus, rememberFocus, resolveCoachFocus } from './coachFocusHelpers'

export type UseCoachFocusParams = {
  userId?: string
  /** The leadership focus is on offer; without it the focus is always the personal one. */
  isLeadershipOffered: boolean
  /** Goal-setting is still to be done; a leader lands on the leadership focus for it until they pick one. */
  isFirstGoalSetting: boolean
}

export type UseCoachFocusResult = {
  focus: GoalScope
  setFocus: (focus: GoalScope) => void
  isLeadershipFocus: boolean
}

/** The chosen focus, remembered per user and overridable once by the link from the My people page. */
export function useCoachFocus({
  userId,
  isLeadershipOffered,
  isFirstGoalSetting
}: UseCoachFocusParams): UseCoachFocusResult {
  const location = useLocation()
  const navigate = useNavigate()
  const [storedFocus] = useState<GoalScope | undefined>(() => readStoredFocus(userId))
  const [chosenFocus, setChosenFocus] = useState<GoalScope | undefined>(undefined)

  const setFocus = useCallback(
    (nextFocus: GoalScope) => {
      setChosenFocus(nextFocus)
      if (userId) rememberFocus(userId, nextFocus)
    },
    [userId]
  )

  const requestedFocus = (location.state as Record<string, unknown> | null)?.[COACH_FOCUS_ROUTE_STATE_KEY]

  // The link from My people asks for the leadership focus; consumed once so a refresh does not reapply it.
  useEffect(() => {
    if (!SUPPORTED_FOCUSES.includes(String(requestedFocus))) return

    navigate(location.pathname, { replace: true, state: null })
    if (isLeadershipOffered) setFocus(requestedFocus as GoalScope)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedFocus, isLeadershipOffered])

  // Goals landing mid-talk end goal-setting, and must not take the page to the other focus with them.
  const isGoalSettingVisitRef = useRef(false)
  if (isLeadershipOffered && isFirstGoalSetting) isGoalSettingVisitRef.current = true

  const focus = resolveCoachFocus({
    isLeadershipOffered,
    isGoalSettingVisit: isGoalSettingVisitRef.current,
    storedFocus,
    chosenFocus
  })

  return { focus, setFocus, isLeadershipFocus: focus === GoalScope.LEADERSHIP }
}
